import { faMoneyCheckDollar } from '@fortawesome/free-solid-svg-icons'
import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  SortDirection,
  UserPermission
} from '@globaleris/ui.library'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { PriceComponentInstancesDataTableModel } from '../../../../../api/Argentia/endpoints/priceComponents'
import { EnumPriceType } from '../../../../../api/oData/enums/enumPriceType'
import { enumToSelectOption, enumToSelectOptionArray } from '../../../../../helpers'
import { getCustomerDomain } from '../../../../../redux'

import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box } from '@material-ui/core'
import { AttachMoney, Delete } from '@material-ui/icons'
import { useHistory } from 'react-router'
import { EnumPrecioUnitario, EnumTipoPeaje } from '../../../../../api'
import { EnumPriceComponentReference, EnumRedondeo } from '../../../../../api/Argentia/enums'
import { EnumPresionTension } from '../../../../../api/oData/enums/enumPresionTension'
import { StandardCardTooltip } from '../../../../../components/shared/standardCard/standardCardToolTip/standardCardToolTip'

interface PriceComponentTypeInstacesDataTableProps {
  isFetching: boolean
  referenceType: EnumPriceComponentReference
  componentPriceType: EnumPriceType
  priceComponentsData: PriceComponentInstancesDataTableModel[]
  isDefaultValueTable?: boolean
  title?: string
  onAddDefaultValueActionHandler?: () => void
  onPriceComponentAddActionHandler: (priceType: EnumPriceType) => void
  onPriceComponentEditActionHandler: (selectedRow: PriceComponentInstancesDataTableModel) => void
  onAdvancedPriceEditActionHandler: (selectedRow: PriceComponentInstancesDataTableModel) => void
  onPriceComponentDeleteActionHandler: (selectedRow: PriceComponentInstancesDataTableModel) => void
}

export const PriceComponentTypeInstacesDataTable: React.FC<
  PriceComponentTypeInstacesDataTableProps
> = ({
  isFetching,
  referenceType,
  componentPriceType,
  priceComponentsData,
  isDefaultValueTable = false,
  title,
  onAddDefaultValueActionHandler,
  onPriceComponentAddActionHandler,
  onPriceComponentEditActionHandler,
  onAdvancedPriceEditActionHandler,
  onPriceComponentDeleteActionHandler
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'priceComponentTypeInstances'])
  const history = useHistory()
  const isOver1000Rows = priceComponentsData?.length >= 1000

  const isParametro = EnumPriceType.Parametro === componentPriceType
  const isApuntamiento = EnumPriceType.Apuntamiento === componentPriceType
  const isParametroPeriodo = EnumPriceType.ParametroPeriodo === componentPriceType
  const isPeaje = EnumPriceComponentReference.Peaje === referenceType
  const isPresionTension = EnumPriceComponentReference.PresionTension === referenceType
  const isLead = EnumPriceComponentReference.Lead === referenceType
  const isRegionImpuestos = EnumPriceComponentReference.RegionImpuestos === referenceType

  // #region Handlers Starts
  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  const priceComponentTypeInstancesDataTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: !isDefaultValueTable,
    elevation: 1,
    filterType: isDefaultValueTable ? FilterType.NONE : FilterType.ADVANCED,
    download: !isDefaultValueTable,
    canEdit: false,
    canDelete: false,
    canInsert:
      (isDefaultValueTable &&
        referenceType !== EnumPriceComponentReference.Oferta &&
        priceComponentsData.length === 0) ||
      (!isDefaultValueTable && (isPresionTension || isPeaje || isLead || isRegionImpuestos)),
    customInsertEditModal: true,

    onRowInsert: async () => {
      if (isDefaultValueTable) {
        onAddDefaultValueActionHandler?.()
      } else {
        onPriceComponentAddActionHandler(componentPriceType)
      }
      return true
    },

    pagination: !isDefaultValueTable,

    analytics: {
      customer: getCustomerDomain(),
      view: t('priceComponentTypeInstances:dataTable.analytics')
    },
    sortOrder: {
      name: 'id',
      direction: SortDirection.ASC
    },
    rowCustomActions: [
      ...(isParametro || isApuntamiento
        ? [
            {
              userPermission: UserPermission.EDIT,
              icon: <FontAwesomeIcon size="sm" icon={faMoneyCheckDollar as IconProp} />,
              tooltip: t('priceComponentTypeInstances:dataTable.customActions.priceComponentEdit'),
              onClick: onPriceComponentEditActionHandler
            },
            {
              userPermission: UserPermission.EDIT,
              icon: <AttachMoney fontSize="small" />,
              tooltip: t('priceComponentTypeInstances:dataTable.customActions.advancedEdit'),
              onClick: onAdvancedPriceEditActionHandler
            },
            {
              userPermission: UserPermission.EDIT,
              icon: <Delete fontSize="small" />,
              tooltip: t(
                'priceComponentTypeInstances:dataTable.customActions.priceComponentDelete'
              ),
              onClick: onPriceComponentDeleteActionHandler
            }
          ]
        : []),
      ...(isParametroPeriodo
        ? [
            {
              userPermission: UserPermission.EDIT,
              icon: <FontAwesomeIcon size="sm" icon={faMoneyCheckDollar as IconProp} />,
              tooltip: t('priceComponentTypeInstances:dataTable.customActions.priceComponentEdit'),
              onClick: onPriceComponentEditActionHandler
            },
            {
              userPermission: UserPermission.EDIT,
              icon: <Delete fontSize="small" />,
              tooltip: t(
                'priceComponentTypeInstances:dataTable.customActions.priceComponentDelete'
              ),
              onClick: onPriceComponentDeleteActionHandler
            }
          ]
        : [])
    ]
  }

  const priceComponentTypeInstancesDataTableColumns: DataTableColumn[] = [
    {
      name: 'id',
      display: false
    },
    {
      name: 'isDefaultValue',
      label: t('priceComponentTypeInstances:dataTable.columns.isDefaultValue'),
      type: DataType.BOOLEAN,
      display: false
    },
    {
      name: 'referenceTypeEntityCode',
      label: t('priceComponentTypeInstances:dataTable.columns.code'),
      type: DataType.TEXT,
      // This entities has no property code available
      display: !(
        referenceType === EnumPriceComponentReference.Flexibilidad ||
        referenceType === EnumPriceComponentReference.Peaje ||
        referenceType === EnumPriceComponentReference.PresionTension
      )
    },
    {
      name: 'referenceTypeEntityDescription',
      label: t('priceComponentTypeInstances:dataTable.columns.description'),
      type: DataType.TEXT
    },
    {
      name: 'price.priceType',
      label: t('priceComponentTypeInstances:dataTable.columns.priceType'),
      selectOptions: enumToSelectOptionArray(EnumPriceType, 'enums:EnumPriceTypes'),
      type: DataType.SELECT
    },
    {
      name: 'presionTension',
      label: t('priceComponentTypeInstances:dataTable.columns.presionTension'),
      selectOptions: enumToSelectOptionArray(EnumPresionTension, 'enums:EnumPresionTension'),
      display: isPresionTension,
      type: DataType.SELECT
    },
    {
      name: 'toll',
      label: t('priceComponentTypeInstances:dataTable.columns.toll'),
      selectOptions: enumToSelectOptionArray(EnumTipoPeaje, 'enums:EnumTipoPeaje'),
      display: isPeaje,
      type: DataType.SELECT
    },
    {
      name: 'price.unitPrice',
      label: t('priceComponentTypeInstances:dataTable.columns.unitPrice'),
      selectOptions: enumToSelectOption(EnumPrecioUnitario),
      type: DataType.SELECT
    },
    {
      name: 'price.rounding',
      label: t('priceComponentTypeInstances:dataTable.columns.rounding'),
      selectOptions: enumToSelectOption(EnumRedondeo),
      type: DataType.SELECT
    }
  ]

  // #endregion DataTable Config Ends

  // #region Hooks Starts
  // #endregion Hooks Ends

  return (
    <Box mb={2}>
      <DataTable
        title={title}
        data={!isFetching ? priceComponentsData : undefined}
        columns={priceComponentTypeInstancesDataTableColumns}
        options={priceComponentTypeInstancesDataTableOptions}
      />
      {isOver1000Rows && (
        <StandardCardTooltip
          tooltipTitle={t('priceComponentTypeInstances:dataTable.tooltips.limitReached.title')}
          tooltipCaption={t('priceComponentTypeInstances:dataTable.tooltips.limitReached.caption')}
        />
      )}
    </Box>
  )
}
